// Launch review + success screen. Two-panel BEFORE / AFTER layout.
//
// BEFORE (always visible): campaign summary + Launch button. Coach reviews what will happen.
// AFTER  (only after launch succeeds): success state + next-action buttons.
//
// The API call moves from happening on the Prospects screen to happening here — the coach
// explicitly reviews before the campaign becomes real.
import { Anchor, Box, Button, Group, Paper, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';

function SummaryRow({ label, value }) {
  return (
    <Group gap="md" wrap="nowrap" align="flex-start">
      <Text size="sm" style={{ color: 'var(--aviary-muted)', width: 100, flexShrink: 0 }}>
        {label}
      </Text>
      <Text size="sm" style={{ color: 'var(--aviary-ink)', flex: 1 }}>
        {value}
      </Text>
    </Group>
  );
}

function firstNamesList(names) {
  const cleaned = names.map((n) => String(n || '').trim().split(/\s+/)[0]).filter(Boolean);
  if (cleaned.length === 0) return '';
  if (cleaned.length === 1) return cleaned[0];
  if (cleaned.length === 2) return `${cleaned[0]} and ${cleaned[1]}`;
  return `${cleaned.slice(0, -1).join(', ')} and ${cleaned[cleaned.length - 1]}`;
}

function channelLine(channel) {
  const c = String(channel || '').toLowerCase();
  if (c === 'linkedin') return 'LinkedIn, email as backup';
  if (c === 'email') return 'Email';
  if (c === 'whatsapp') return 'WhatsApp, SMS as backup';
  if (c === 'sms') return 'Text message';
  return c || 'Not set';
}

function friendlyCampaignName(campaign) {
  const raw = String(campaign?.name || '').trim();
  if (raw && !/^untitled$/i.test(raw)) return raw;
  const brief = campaign?.brief || {};
  const who = Array.isArray(brief?.answers?.who) ? brief.answers.who[0] : '';
  const where = brief?.answers?.where?.text || '';
  if (who && where) return `${who} in ${where}`;
  return raw || 'Your campaign';
}

export default function LaunchedScreen({
  campaign,
  selectedProspects = [],
  touches = [],
  launched = false,
  launching = false,
  importedCount = 0,
  onLaunch,
  onBack,
  onSeePipeline,
  onAddMoreProspects,
  launchLabel = 'Launch campaign »',
}) {
  const peopleCount = selectedProspects.length;
  const followUpCount = Math.max(0, touches.length - 1);
  const displayedNames = firstNamesList(
    selectedProspects.slice(0, 5).map((p) => p.name || '')
  );

  return (
    <Group align="flex-start" wrap="nowrap" gap="xl" style={{ width: '100%' }}>
      {/* BEFORE panel */}
      <Paper
        p="xl"
        radius="md"
        withBorder
        style={{
          flex: 1,
          background: 'var(--aviary-surface)',
          opacity: launched ? 0.7 : 1,
          minWidth: 0,
        }}
      >
        <Stack gap="lg">
          <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.5 }}>
            BEFORE
          </Text>
          <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 24, lineHeight: 1.25 }}>
            Ready when you are.
          </Text>

          <Paper
            p="md"
            radius="md"
            style={{ background: 'var(--aviary-canvas)', border: '1px solid var(--aviary-disabled)' }}
          >
            <Stack gap="sm">
              <SummaryRow label="campaign" value={friendlyCampaignName(campaign)} />
              <SummaryRow
                label="people"
                value={`${peopleCount} ${peopleCount === 1 ? 'person' : 'people'} — all approved by you`}
              />
              <SummaryRow label="channel" value={channelLine(campaign?.channel)} />
              <SummaryRow
                label="follow-ups"
                value={
                  followUpCount === 0
                    ? 'None — one touch only'
                    : `${followUpCount}, spaced a few days apart`
                }
              />
              <SummaryRow label="approvals" value="you send each message from your pipeline" />
            </Stack>
          </Paper>

          <Text size="sm" style={{ color: 'var(--aviary-muted)', lineHeight: 1.55 }}>
            Nothing sends automatically. You&apos;ll work each lead from your pipeline using the outreach
            buttons — the drafts are ready to go and personalised per person.
          </Text>

          {!launched && (
            <Group justify="space-between" align="center" mt="xs">
              <Anchor
                component="button"
                type="button"
                size="sm"
                c="dimmed"
                onClick={onBack}
                style={{ background: 'none', border: 'none' }}
              >
                ← Prospects
              </Anchor>
              <Button
                color="sage"
                size="md"
                loading={launching}
                disabled={launching || peopleCount === 0}
                onClick={onLaunch}
              >
                {launchLabel}
              </Button>
            </Group>
          )}
        </Stack>
      </Paper>

      {/* AFTER panel */}
      {launched && (
        <Paper
          p="xl"
          radius="md"
          withBorder
          style={{ flex: 1, background: 'var(--aviary-surface)', minWidth: 0 }}
        >
          <Stack gap="lg">
            <Text size="xs" fw={600} style={{ color: 'var(--aviary-muted)', letterSpacing: 0.5 }}>
              AFTER — THE WIN
            </Text>

            <Box
              style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'var(--mantine-color-sage-0)',
                border: '2px solid var(--mantine-color-sage-6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconCheck size={28} color="var(--mantine-color-sage-6)" />
            </Box>

            <Stack gap={6}>
              <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 24, lineHeight: 1.25 }}>
                You&apos;re flying.
              </Text>
              <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 24, lineHeight: 1.25 }}>
                Let&apos;s start prospecting!
              </Text>
            </Stack>

            <Paper
              p="md"
              radius="md"
              style={{ background: 'var(--aviary-canvas)', border: '1px solid var(--aviary-disabled)' }}
            >
              <Text size="sm" style={{ color: 'var(--aviary-ink)', lineHeight: 1.55 }}>
                {displayedNames && (
                  <>
                    <Text component="span" fw={600} inherit>{displayedNames}</Text>
                    {selectedProspects.length > 5 && ` and ${selectedProspects.length - 5} others`}
                    {' '}are in your pipeline.
                    {' '}
                  </>
                )}
                {!displayedNames && `${importedCount} ${importedCount === 1 ? 'prospect' : 'prospects'} added to your pipeline. `}
                Head over to work them one at a time — each message is drafted and ready.
              </Text>
            </Paper>

            <Stack gap="xs">
              <Button variant="default" color="gray" onClick={onSeePipeline}>
                See my pipeline
              </Button>
              <UnstyledButton onClick={onAddMoreProspects}>
                <Text size="sm" c="sage" ta="center" py={8}>
                  Add more prospects
                </Text>
              </UnstyledButton>
            </Stack>
          </Stack>
        </Paper>
      )}
    </Group>
  );
}
